import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ChefHat, Flame, Leaf, Soup, Cookie, Salad } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultTechniques = [
  {
    title: { fi: 'Liemien Valmistus', fr: 'Préparation de Bouillons', en: 'Making Broths' },
    content: {
      fi: 'Hyvä kasvisliemi on monen ruoan perusta. Käytä sipulia, porkkanaa, selleriä, yrttejä ja mausteita. Keitä hitaasti 1-2 tuntia.',
      fr: 'Un bon bouillon de légumes est la base de nombreux plats. Utilisez oignon, carotte, céleri, herbes et épices. Faites mijoter 1-2 heures.',
      en: 'A good vegetable broth is the foundation of many dishes. Use onion, carrot, celery, herbs and spices. Simmer slowly for 1-2 hours.'
    }
  },
  {
    title: { fi: 'Tofun Käsittely', fr: 'Préparation du Tofu', en: 'Tofu Preparation' },
    content: {
      fi: 'Purista tofu kuivaksi, leikkaa kuutioiksi ja marinoi vähintään 30 minuuttia. Paista korkealla lämmöllä rapeaksi tai paahda uunissa.',
      fr: 'Pressez le tofu pour le sécher, coupez en cubes et marinez au moins 30 minutes. Faites frire à feu vif ou rôtissez au four.',
      en: 'Press tofu dry, cut into cubes and marinate for at least 30 minutes. Fry at high heat until crispy or roast in the oven.'
    }
  },
  {
    title: { fi: 'Vegaaninen Leivonta', fr: 'Pâtisserie Végane', en: 'Vegan Baking' },
    content: {
      fi: 'Korvaa munat pellavansiemenillä (1 rkl + 3 rkl vettä = 1 muna), banaanilla tai omenasoseella. Käytä kasvimaito kermaan.',
      fr: "Remplacez les œufs par des graines de lin (1 c.s. + 3 c.s. d'eau = 1 œuf), de la banane ou de la compote de pommes. Utilisez du lait végétal pour la crème.",
      en: 'Replace eggs with flaxseeds (1 tbsp + 3 tbsp water = 1 egg), banana, or applesauce. Use plant milk for cream.'
    }
  },
  {
    title: { fi: 'Kasvisten Paahto', fr: 'Rôtissage des Légumes', en: 'Roasting Vegetables' },
    content: {
      fi: 'Paahda kasvikset 200°C:ssa öljyllä ja suolalla. Älä täytä pellillä liikaa. Anna karamelloitua 25-40 minuuttia.',
      fr: 'Rôtissez les légumes à 200°C avec de l\'huile et du sel. Ne surchargez pas la plaque. Laissez caraméliser 25-40 minutes.',
      en: 'Roast vegetables at 200°C with oil and salt. Don\'t overcrowd the pan. Let caramelize for 25-40 minutes.'
    }
  },
  {
    title: { fi: 'Marinoiminen', fr: 'Marinage', en: 'Marinating' },
    content: {
      fi: 'Hyvä marinadi sisältää happoa (sitruuna, etikka), öljyä, suolaa ja mausteita. Anna marinoitua vähintään tunnin.',
      fr: 'Une bonne marinade contient de l\'acide (citron, vinaigre), de l\'huile, du sel et des épices. Laissez mariner au moins une heure.',
      en: 'A good marinade contains acid (lemon, vinegar), oil, salt and spices. Marinate for at least an hour.'
    }
  },
  {
    title: { fi: 'Umami Maku', fr: 'Saveur Umami', en: 'Umami Flavor' },
    content: {
      fi: 'Lisää umamia soijakastikkeella, miso-tahnalla, hiivahiutaleilla, tomaattipyreellä tai sienillä. Tämä tekee ruoasta täyteläistä.',
      fr: 'Ajoutez de l\'umami avec de la sauce soja, du miso, de la levure nutritionnelle, du concentré de tomates ou des champignons.',
      en: 'Add umami with soy sauce, miso paste, nutritional yeast, tomato paste, or mushrooms. This makes food satisfying.'
    }
  }
];

export const Techniques = () => {
  const { t, getLocalizedText } = useLanguage();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages/techniques`);
      setPageData(response.data);
    } catch (error) {
      console.error('Error fetching page content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use DB content or defaults
  const title = pageData?.title || { fi: 'Vegaaniset Ruoanlaittotekniikat', fr: 'Techniques de Cuisine Végane', en: 'Vegan Cooking Techniques' };
  const intro = pageData?.content || { 
    fi: 'Opi vegaanisen ruoanlaiton perusteet ja edistyneet tekniikat.',
    fr: 'Apprenez les bases et les techniques avancées de la cuisine végétalienne.',
    en: 'Learn the basics and advanced techniques of vegan cooking.'
  };
  
  const sections = (pageData?.sections && pageData.sections.length > 0) ? pageData.sections : defaultTechniques;
  const icons = [Soup, Leaf, Cookie, Flame, Salad, ChefHat];

  return (
    <div className="min-h-screen py-12" data-testid="techniques-page">
      {/* Hero */}
      <section className="py-20 mb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="font-accent text-2xl text-secondary">
              {getLocalizedText({
                fi: 'Kokin vinkit',
                fr: 'Conseils du chef',
                en: "Chef's tips"
              })}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mt-2 mb-6">
              {getLocalizedText(title)}
            </h1>
            <p className="text-xl text-foreground/70 leading-relaxed">
              {getLocalizedText(intro)}
            </p>
          </div>
        </div>
      </section>

      {/* Techniques Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, index) => {
              const Icon = icons[index % icons.length];
              return (
                <div 
                  key={index}
                  className="group p-8 rounded-3xl bg-card border border-border hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                  data-testid={`technique-${index}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-muted/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">
                    {getLocalizedText(section.title)}
                  </h3>
                  <p className="text-foreground/60 leading-relaxed">
                    {getLocalizedText(section.content)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pro Tip */}
      <section className="py-16 bg-muted/20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="bg-card rounded-3xl p-8 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <ChefHat className="h-8 w-8 text-primary" />
              <span className="font-accent text-2xl text-primary">
                {getLocalizedText({
                  fi: 'Ammattilaisvinkki',
                  fr: 'Conseil de pro',
                  en: 'Pro Tip'
                })}
              </span>
            </div>
            <p className="text-foreground/70 leading-relaxed">
              {getLocalizedText({
                fi: 'Mausta ruokaa vaiheittain ruoanlaiton aikana, älä vain lopussa. Tämä rakentaa kerroksia makuun ja tekee ruoasta paljon maukkaampaa!',
                fr: "Assaisonnez progressivement pendant la cuisson, pas seulement à la fin. Cela construit des couches de saveur et rend le plat bien plus savoureux!",
                en: 'Season your food in stages throughout cooking, not just at the end. This builds layers of flavor and makes the dish much more delicious!'
              })}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
